/**
 * CON-13009: pure helpers for ScenarioApply OAuth return / redirect wizard positioning.
 * Kept free of React/Relay so unit tests can cover each redirect scenario.
 */

export function matchesGrantedChannelDef(item, grantedChannelDefId) {
    if (!grantedChannelDefId || !item || !item.channelDef) {
        return false;
    }

    return item.channelDef.realId === grantedChannelDefId ||
        item.channelDef.id === grantedChannelDefId ||
        item.groupKey === grantedChannelDefId;
}

export function findGrantedOrgChannel(channels, grantedChannelId) {
    if (!grantedChannelId || !channels || !channels.edges) {
        return null;
    }

    const edge = channels.edges.find(({node}) =>
        node.realId === grantedChannelId || node.id === grantedChannelId
    );

    return edge ? edge.node : null;
}

/**
 * After OAuth the channel may exist in org.channels but still sit in channelsToConnect
 * (Relay cache / groupKey timing). Promote that group to alreadyConnected so the wizard
 * shows "Connected/Configure" instead of "Connect {app}" and can skip on grantedStep=5.
 */
export function promoteGrantedChannelFromConnectList(alreadyConnectedChannels, channelsToConnect, channels, grantedChannelId, grantedChannelDefId) {
    if (!grantedChannelId || !grantedChannelDefId) {
        return {alreadyConnectedChannels, channelsToConnect};
    }

    const connectIdx = channelsToConnect.findIndex((item) =>
        matchesGrantedChannelDef(item, grantedChannelDefId)
    );

    if (connectIdx < 0) {
        return {alreadyConnectedChannels, channelsToConnect};
    }

    const nextConnect = channelsToConnect.slice();
    const [removed] = nextConnect.splice(connectIdx, 1);
    const grantedNode = findGrantedOrgChannel(channels, grantedChannelId);
    const channel = grantedNode || {
        realId: grantedChannelId,
        id: grantedChannelId,
        channelDef: removed.channelDef,
        name: null,
        activated: false,
    };

    return {
        alreadyConnectedChannels: alreadyConnectedChannels.concat([{
            groupKey: removed.groupKey,
            channelDef: removed.channelDef,
            channel,
        }]),
        channelsToConnect: nextConnect,
    };
}

/** Outside scenario, grantedStep=5 lands on add-step/5 (ActivateChannel). Same is required here. */
export function channelNeedsActivationAfterGrant(channel) {
    return !channel || channel.activated !== true;
}

export function shouldStayOnGrantedChannel(grantedStep, channel) {
    if (grantedStep === 4) {
        return true;
    }

    return grantedStep === 5 && channelNeedsActivationAfterGrant(channel);
}

export function getGrantedChannelConnectStep(grantedStep, channel) {
    if (grantedStep === 4) {
        return 4;
    }

    if (grantedStep === 5 && channelNeedsActivationAfterGrant(channel)) {
        return 5;
    }

    return grantedStep;
}

/**
 * Backend GrantAccess sets grantedStep=4 when hasOptionsAfterGrant, else 5.
 * Frontend maps that to wizard position:
 * - stay on granted channel index (+0) when post-grant options or activation still needed
 * - otherwise advance to the next wizard step (+1)
 */
export function getGrantedWizardStepOffset(grantedStep, channel) {
    return shouldStayOnGrantedChannel(grantedStep, channel) ? 0 : 1;
}

/**
 * Resolve configurationStep index after OAuth return.
 * Wizard order: [prefix...] + alreadyConnected + channelsToConnect + channelsToSelect + ...
 */
export function resolveGrantedConfigurationStep({
    prefixSteps = 0,
    grantedStep,
    grantedChannelId,
    grantedChannelDefId,
    alreadyConnectedChannels = [],
    channelsToConnect = [],
    channelsToSelect = [],
    orgChannels = null,
} = {}) {
    if (!grantedChannelId) {
        return {configurationStep: null, alreadyConnectedChannels, channelsToConnect, channelSelect: {}};
    }

    let nextAlready = alreadyConnectedChannels;
    let nextConnect = channelsToConnect;
    const channelSelect = {};

    if (grantedChannelDefId) {
        const promoted = promoteGrantedChannelFromConnectList(
            nextAlready,
            nextConnect,
            orgChannels,
            grantedChannelId,
            grantedChannelDefId
        );
        nextAlready = promoted.alreadyConnectedChannels;
        nextConnect = promoted.channelsToConnect;
    }

    const channelMatches = (item) =>
        item.channel && (
            item.channel.realId === grantedChannelId ||
            item.channel.id === grantedChannelId
        );

    let grantedIdx = nextAlready.findIndex(channelMatches);
    if (grantedIdx < 0 && grantedChannelDefId) {
        // Same channelDef group may already hold a *different* connection. Prefer the
        // granted channel id (org cache or synthetic) so OAuth return never steals an
        // older sibling connection of the same type.
        const defIdx = nextAlready.findIndex((item) =>
            matchesGrantedChannelDef(item, grantedChannelDefId)
        );
        if (defIdx >= 0) {
            const existing = nextAlready[defIdx];
            const grantedNode = findGrantedOrgChannel(orgChannels, grantedChannelId);
            nextAlready = nextAlready.slice();
            nextAlready[defIdx] = {
                ...existing,
                channel: grantedNode || {
                    realId: grantedChannelId,
                    id: grantedChannelId,
                    channelDef: existing.channelDef,
                    name: null,
                    activated: false,
                },
            };
            grantedIdx = defIdx;
        }
    }

    if (grantedIdx >= 0) {
        const grantedItem = nextAlready[grantedIdx];
        channelSelect[grantedItem.groupKey] = grantedItem.channel;
        const offset = getGrantedWizardStepOffset(grantedStep, grantedItem.channel);
        return {
            configurationStep: prefixSteps + grantedIdx + offset,
            alreadyConnectedChannels: nextAlready,
            channelsToConnect: nextConnect,
            channelSelect,
            stayOnChannel: offset === 0,
            connectStep: getGrantedChannelConnectStep(grantedStep, grantedItem.channel),
        };
    }

    const selectIdx = channelsToSelect.findIndex((item) => {
        if (item.channels && item.channels.some(
            (ch) => ch.realId === grantedChannelId || ch.id === grantedChannelId
        )) {
            return true;
        }
        return matchesGrantedChannelDef(item, grantedChannelDefId);
    });

    if (selectIdx >= 0) {
        const selectGroup = channelsToSelect[selectIdx];
        const grantedChannel = (selectGroup.channels || []).find(
            (ch) => ch.realId === grantedChannelId || ch.id === grantedChannelId
        );
        if (grantedChannel) {
            channelSelect[selectGroup.groupKey] = grantedChannel;
        }
        const offset = getGrantedWizardStepOffset(grantedStep, grantedChannel);
        return {
            configurationStep: prefixSteps + nextAlready.length + nextConnect.length + selectIdx + offset,
            alreadyConnectedChannels: nextAlready,
            channelsToConnect: nextConnect,
            channelSelect,
            stayOnChannel: offset === 0,
            connectStep: getGrantedChannelConnectStep(grantedStep, grantedChannel),
        };
    }

    return {
        configurationStep: prefixSteps,
        alreadyConnectedChannels: nextAlready,
        channelsToConnect: nextConnect,
        channelSelect,
        stayOnChannel: false,
        connectStep: null,
    };
}

/** Mirrors GrantAccess callback routing for scenario context. */
export function grantedStepFromHasOptionsAfterGrant(hasOptionsAfterGrant) {
    return hasOptionsAfterGrant ? 4 : 5;
}
